import { useState } from "react";
import { BellRing, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, Spinner } from "@/components/Field";
import { usePush } from "@/hooks/usePush";
import { isIos, isStandalone } from "@/lib/push";

const IosHint = () => (
  <p data-testid="push-ios-hint" className="text-sm text-amber-900 bg-amber-50 rounded-lg px-3 py-2">
    Su iPhone/iPad: apri il menu Condividi e scegli «Aggiungi alla schermata Home», poi apri l'app da lì per attivare le notifiche.
  </p>
);

export function PushBanner() {
  const p = usePush();
  const [hidden, setHidden] = useState(localStorage.getItem("ag_push_banner") === "off");
  const iosBlocked = isIos() && !isStandalone();
  const show = !hidden && p.permission !== "denied" && (iosBlocked || (p.supported && p.subscribed === false));
  if (!show) return null;
  const dismiss = () => { localStorage.setItem("ag_push_banner", "off"); setHidden(true); };
  return (
    <Card testId="push-banner" className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 border-primary/30 bg-primary/5">
      <BellRing className="h-6 w-6 text-primary shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="font-semibold text-slate-900">Attiva le notifiche push</div>
        <p className="text-sm text-slate-600">Ricevi subito compiti, spesa, prenotazioni, rimborsi e scadenze anche ad app chiusa.</p>
        {iosBlocked && <IosHint />}
      </div>
      <div className="flex gap-2">
        {!iosBlocked && <Button data-testid="push-banner-enable" className="h-11" disabled={p.busy} onClick={p.enable}>{p.busy ? <Spinner className="h-4 w-4" /> : "Attiva"}</Button>}
        <Button data-testid="push-banner-dismiss" variant="ghost" size="icon" className="h-11 w-11" onClick={dismiss} aria-label="Chiudi"><X className="h-4 w-4" /></Button>
      </div>
    </Card>
  );
}

export function PushSettingsCard() {
  const p = usePush();
  const iosBlocked = isIos() && !isStandalone();
  return (
    <Card testId="push-settings-card" className="p-5 space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-semibold">Notifiche push</h2>
          <p className="text-sm text-slate-500">Avvisi su questo dispositivo, anche ad app chiusa.</p>
        </div>
        <Switch data-testid="push-toggle" checked={!!p.subscribed} disabled={!p.supported || p.busy || p.subscribed === null}
          onCheckedChange={(v) => (v ? p.enable() : p.disable())} />
      </div>
      {iosBlocked && <IosHint />}
      {!p.supported && !iosBlocked && <p data-testid="push-unsupported" className="text-sm text-slate-500">Questo browser non supporta le notifiche push.</p>}
      {p.permission === "denied" && <p data-testid="push-denied" className="text-sm text-red-700">Le notifiche sono bloccate: sbloccale dalle impostazioni del browser per questo sito.</p>}
      {p.subscribed && <Button data-testid="push-test" variant="outline" className="h-11" disabled={p.busy} onClick={p.test}>Invia notifica di prova</Button>}
    </Card>
  );
}
